import { useEffect } from "react";
import { Link } from "react-router-dom";
import { NoirBar, NoirFooter } from "./NoirChrome";
import { formatEffectiveDate, legalDocuments, parseInline, type LegalBlock, type LegalDocument } from "./legalContent";
import "./MarketingPage.css";
import "./LegalPage.css";

// Privacy policy and terms of service, rendered from typed content in the landing page's black system:
// serif title, mono effective date, a contents list, and prose at a 68ch measure.

export function LegalPage({ slug }: { slug: LegalDocument["slug"] }) {
  const document_ = legalDocuments[slug];

  useEffect(() => {
    document.title = `${document_.title} · LoopGroup`;
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [document_]);

  return (
    <div className="noir legal">
      <NoirBar />
      <main className="legal__main">
        <header className="legal__head">
          <p className="noir__eyebrow">{document_.eyebrow}</p>
          <h1 className="legal__title">{document_.title}</h1>
          <p className="legal__lede">{document_.lede}</p>
          <p className="legal__effective">
            <span>Effective</span> <time dateTime={document_.effectiveDate}>{formatEffectiveDate(document_.effectiveDate)}</time>
          </p>
        </header>

        <div className="legal__body">
          <nav className="legal__contents" aria-label="Contents">
            <p className="legal__contents-label">Contents</p>
            <ol>
              {document_.sections.map((section, index) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}><span>{String(index + 1).padStart(2, "0")}</span>{section.heading}</a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="legal__prose">
            {/* The governing-law section of the terms is a placeholder (Texas) until counsel confirms the jurisdiction. */}
            {document_.sections.map((section, index) => (
              <section key={section.id} id={section.id} aria-labelledby={`${section.id}-heading`}>
                <h2 id={`${section.id}-heading`}><span className="legal__index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>{section.heading}</h2>
                {section.blocks.map((block, blockIndex) => <Block key={blockIndex} block={block} />)}
              </section>
            ))}
          </article>
        </div>
      </main>
      <NoirFooter />
    </div>
  );
}

function Block({ block }: { block: LegalBlock }) {
  if (block.kind === "paragraph") return <p><Inline text={block.text} /></p>;
  return (
    <ul>
      {block.items.map((item, index) => <li key={index}><Inline text={item} /></li>)}
    </ul>
  );
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((segment, index) => {
        if (segment.kind === "text") return segment.text;
        if (segment.href.startsWith("/")) return <Link key={index} to={segment.href}>{segment.text}</Link>;
        return <a key={index} href={segment.href}>{segment.text}</a>;
      })}
    </>
  );
}
